import { unescape } from 'lodash'
import memoize from 'memoize-one'
import { Component } from 'react'
import styled from 'styled-components'

import * as emojiConverter from '@/utilities/client-components/utilities/emojiConverter'
import * as themeUtilities from '@/utilities/client-components/utilities/themeUtils'

import TextFormatter from './TextFormatters'

const StyledHashmarkBlock = styled.div`
  margin: -21px 0 0;
  display: flex;
  justify-content: space-between;

  &.edge {
    margin: -27px 0 0;
  }

  &.ie11 {
    margin: -29px 0 0;
  }
`
const StyledHashmark = styled.div`
  width: 20px;
  height: 20px;
  background: ${({ hidden, theme }: any) =>
    hidden ? 'transparent' : themeUtilities.getColor(theme, 'default', 'background')};
  border-radius: 50%;
  border: 2px solid ${({ theme }: any) => themeUtilities.getColor(theme, 'default', 'link')};
  opacity: ${({ selected }: any) => (selected ? 0 : 1)};
  display: flex;
  cursor: pointer;
  z-index: 3;
`

const StyledHashLabelBlock = styled.div`
  margin: 3px 0;
  padding-top: 5px;
  display: flex;
  justify-content: space-between;
  align-content: flex-end;
  text-align: center;
`
const StyledHashLabel = styled.div`
  color: ${(props: any) =>
    props.selected
      ? themeUtilities.getColor(props.theme, 'default', 'primary')
      : themeUtilities.getColor(props.theme, 'default')};
  font-size: ${(props: any) => themeUtilities.getFontSize(props.theme, 'default')}px;
  margin: 2px;
  flex: 1;
  cursor: pointer;
  display: flex;

  :first-child {
    text-align: left;
  }

  :last-child {
    text-align: right;
  }
`

interface RangeHashMarksProps {
  className?: string
  hashmarks: {
    value: number
    label?: string
  }[]
  leftPad?: boolean
  max: number
  min: number
  onClick(...args: unknown[]): unknown
  selectedValue: string
  step?: string | number
}

class RangeHashMarks extends Component<RangeHashMarksProps> {
  static displayName = 'RangeHashMarks'

  static defaultProps = {
    hashmarks: [],
    min: 0,
    max: 10,
    step: 1,
    onClick: undefined,
    selectedValue: undefined,
    className: ''
  }

  memoizedHashes: any

  constructor(props: any) {
    super(props)

    this.memoizedHashes = memoize((hashmarks: any, min: any, max: any, step: any) =>
      calculateHashes(hashmarks, min, max, step)
    )
  }

  render() {
    const { onClick, selectedValue, hashmarks, max, min, step, className } = this.props
    // To reduce recalculations we memoize this (potential use case for other components as well)
    const hashes = this.memoizedHashes(hashmarks, min, max, step)

    return (
      <>
        <StyledHashmarkBlock className={className}>
          {hashes.map((hash: any) => (
            <StyledHashmark
              hidden={hash.hidden}
              // @ts-expect-error
              selected={selectedValue.toString() === hash.value.toString()}
              key={hash.value}
              onClick={() => onClick(hash.value)}
              data-testid="hashmark"
            />
          ))}
        </StyledHashmarkBlock>
        <StyledHashLabelBlock className={className}>
          {hashes.map((hash: any) => (
            <StyledHashLabel
              data-id={'wg-hash-label'}
              // @ts-expect-error
              selected={selectedValue.toString() === hash.value.toString()}
              key={hash.value}
              onClick={() => onClick(hash.value)}
              data-testid="hashlabel"
            >
              {emojiConverter.toEmoji(TextFormatter(unescape(hash.label)))}
            </StyledHashLabel>
          ))}
        </StyledHashLabelBlock>
      </>
    )
  }
}

export default RangeHashMarks

/**
 * Given the hashmark definitions, this function moves through each step and transforms
 * the step into a hash.  Steps that are not defined in the hashmarks array are "hidden" hashmarks
 * @param hashmarks - the hashmarks from props
 * @param min - minimum step value
 * @param max - maximum step value
 * @param step - step interval
 * @returns {Array} - All steps defined as hashes
 */
export function calculateHashes(hashmarks: any, min: any, max: any, step: any) {
  let currentStepValue = min
  let actualStep = 1
  if (!!step && step !== 'any') {
    // if step is truthy (0 is falsy and not a valid step)
    actualStep = step
  }
  const stepsAsHashes = []
  while (currentStepValue <= max) {
    const hashDefinitionForStep = hashmarks.find((h: any) => h.value === currentStepValue)
    if (hashDefinitionForStep) {
      stepsAsHashes.push(hashDefinitionForStep)
    } else {
      stepsAsHashes.push({ value: currentStepValue, label: '', hidden: true })
    }

    currentStepValue = currentStepValue + actualStep
  }

  return stepsAsHashes
}
